import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/apiService';

interface Source {
  name: string;
  category: string;
  url: string;
  country?: string;
  flag?: string;
  enabled: boolean;
  is_custom: boolean;
}

const CATEGORIES = [
  'All',
  'RSS Feeds',
  'Market Data',
  'Company Reports',
  'Government & Regulatory',
  'Custom',
];

const CATEGORY_ICONS: Record<string, string> = {
  'RSS Feeds': 'rss_feed',
  'Market Data': 'trending_up',
  'Company Reports': 'business',
  'Government & Regulatory': 'account_balance',
  'Custom': 'add_circle',
  'All': 'list',
};

const SourcesPage: React.FC = () => {
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  // Add source form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newCategory, setNewCategory] = useState('RSS Feeds');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');

  const loadSources = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getSources();
      setSources(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Failed to load sources. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSources(); }, [loadSources]);

  const handleToggle = async (source: Source) => {
    setActionLoading(source.url);
    try {
      await apiService.toggleSource(source.url, source.is_custom, !source.enabled);
      setSources(prev =>
        prev.map(s => s.url === source.url ? { ...s, enabled: !source.enabled } : s)
      );
    } catch {
      setError('Failed to update source.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (source: Source) => {
    if (!window.confirm(`Remove "${source.name}"?`)) return;
    setActionLoading(source.url);
    try {
      await apiService.removeSource(source.url, source.is_custom);
      setSources(prev => prev.filter(s => s.url !== source.url));
    } catch {
      setError('Failed to remove source.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddSource = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    setAddSuccess('');
    if (!newName.trim()) return setAddError('Name is required');
    if (!newUrl.trim()) return setAddError('URL is required');
    if (!newUrl.startsWith('http')) return setAddError('URL must start with http:// or https://');

    setAddLoading(true);
    try {
      await apiService.addSource(newName.trim(), newUrl.trim(), newCategory);
      setAddSuccess(`"${newName.trim()}" added successfully`);
      setNewName(''); setNewUrl(''); setNewCategory('RSS Feeds');
      await loadSources();
      setTimeout(() => { setAddSuccess(''); setShowAddForm(false); }, 1500);
    } catch (e: any) {
      setAddError(e?.message || 'Failed to add source');
    } finally {
      setAddLoading(false);
    }
  };

  // Filtered list
  const filtered = sources.filter(s => {
    const matchCat = filterCategory === 'All' || s.category === filterCategory;
    const matchSearch = !search ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.url.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const enabledCount = sources.filter(s => s.enabled).length;
  const customCount = sources.filter(s => s.is_custom).length;

  // Group for display
  const grouped = CATEGORIES.slice(1).reduce<Record<string, Source[]>>((acc, cat) => {
    const items = filtered.filter(s => s.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});
  const ungrouped = filtered.filter(s => !CATEGORIES.slice(1).includes(s.category));
  if (ungrouped.length) grouped['Other'] = ungrouped;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 }}>News Sources</h1>
          <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0' }}>
            {enabledCount} active · {sources.length} total · {customCount} custom
          </p>
        </div>
        <button
          onClick={() => { setShowAddForm(v => !v); setAddError(''); setAddSuccess(''); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: showAddForm ? '#f1f5f9' : '#4f46e5', color: showAddForm ? '#475569' : '#fff',
            fontSize: 14, fontWeight: 600, transition: 'all 0.15s',
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            {showAddForm ? 'close' : 'add'}
          </span>
          {showAddForm ? 'Cancel' : 'Add Source'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Add source form */}
      {showAddForm && (
        <form onSubmit={handleAddSource} style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
          padding: 20, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Add New Source</h3>
          {addError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', color: '#dc2626', fontSize: 13, marginBottom: 12 }}>
              {addError}
            </div>
          )}
          {addSuccess && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '8px 12px', color: '#15803d', fontSize: 13, marginBottom: 12 }}>
              ✓ {addSuccess}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
                Source Name *
              </label>
              <input
                value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Reuters"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
                Feed URL *
              </label>
              <input
                value={newUrl} onChange={e => setNewUrl(e.target.value)}
                placeholder="https://feeds.reuters.com/..."
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
                Category
              </label>
              <select
                value={newCategory} onChange={e => setNewCategory(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none', background: '#fff' }}
              >
                {CATEGORIES.slice(1).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
            <button
              type="submit" disabled={addLoading}
              style={{
                padding: '9px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: '#4f46e5', color: '#fff', fontSize: 13, fontWeight: 600,
                opacity: addLoading ? 0.6 : 1,
              }}
            >
              {addLoading ? 'Adding…' : 'Add Source'}
            </button>
            <button
              type="button" onClick={() => { setShowAddForm(false); setAddError(''); setNewName(''); setNewUrl(''); }}
              style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer', background: '#fff', fontSize: 13, color: '#64748b' }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: 200 }}>
          <span className="material-symbols-outlined" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#94a3b8' }}>search</span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search sources…"
            style={{ width: '100%', padding: '8px 10px 8px 34px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none', background: '#fff' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              style={{
                padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: filterCategory === cat ? '#4f46e5' : '#f1f5f9',
                color: filterCategory === cat ? '#fff' : '#475569',
                transition: 'all 0.15s',
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Source list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          Loading sources…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 40, display: 'block', marginBottom: 8 }}>search_off</span>
          No sources match your filter
        </div>
      ) : (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} style={{ marginBottom: 28 }}>
            {/* Category header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#94a3b8' }}>
                {CATEGORY_ICONS[cat] || 'folder'}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                {cat}
              </span>
              <span style={{ fontSize: 11, color: '#cbd5e1', marginLeft: 4 }}>
                {items.filter(s => s.enabled).length}/{items.length} active
              </span>
            </div>

            {/* Source rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map(source => (
                <div
                  key={source.url}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                    padding: '12px 16px',
                    opacity: source.enabled ? 1 : 0.55,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {/* Flag / icon */}
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                    {source.flag || (source.is_custom ? '⚙' : '📡')}
                  </div>

                  {/* Name + URL */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{source.name}</span>
                      {source.is_custom && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#f3e8ff', border: '1px solid #e9d5ff', padding: '1px 7px', borderRadius: 10 }}>
                          CUSTOM
                        </span>
                      )}
                    </div>
                    <a
                      href={source.url} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 380 }}
                    >
                      {source.url}
                    </a>
                  </div>

                  {/* Status label */}
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 20,
                    background: source.enabled ? '#f0fdf4' : '#f8fafc',
                    color: source.enabled ? '#15803d' : '#94a3b8',
                    border: `1px solid ${source.enabled ? '#bbf7d0' : '#e2e8f0'}`,
                    minWidth: 56, textAlign: 'center',
                  }}>
                    {source.enabled ? 'Active' : 'Blocked'}
                  </span>

                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(source)}
                    disabled={actionLoading === source.url}
                    title={source.enabled ? 'Click to block' : 'Click to allow'}
                    style={{
                      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', padding: 0,
                      background: source.enabled ? '#4f46e5' : '#e2e8f0',
                      position: 'relative', transition: 'background 0.2s',
                      opacity: actionLoading === source.url ? 0.5 : 1,
                      flexShrink: 0,
                    }}
                  >
                    <span style={{
                      display: 'block', width: 18, height: 18, borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: 3,
                      left: source.enabled ? 23 : 3,
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </button>

                  {/* Remove (custom only) */}
                  {source.is_custom && (
                    <button
                      onClick={() => handleRemove(source)}
                      disabled={actionLoading === source.url}
                      title="Remove source"
                      style={{
                        width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer', padding: 0,
                        background: 'transparent', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: actionLoading === source.url ? 0.5 : 1,
                        flexShrink: 0,
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#fef2f2')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default SourcesPage;
